import { StaticImage } from 'gatsby-plugin-image';
import type { FC } from 'react';
import { SwiperSlide } from 'swiper/react';

import { AddOnsTemplate } from '@/components/add-ons/template';
import { GalleriesItem } from '@/components/galleries/item';
import { GalleriesList } from '@/components/galleries/list';
import { ComponentsHead } from '@/components/head';
import { LinkWithTitle } from '@/components/links';
import { AnchorWithTitle } from '@/components/links/anchor';

export const PROFESSIONAL_PHOTOGRAPHER_TITLE =
  'Professional Photographer';

export const Head = () => {
  return (
    <ComponentsHead
      title="Hire a Photographer for Your Boat Day | TBBA Add-On"
      description="Capture the best moments of your cruise with a pro photographer on board. From champagne pops to sunset shots—no missed memories."
      keywords="Photography, Add-On, Event Memories, Professional"
    />
  );
};

const AddOnsProfessionalPhotographer: FC = () => {
  return (
    <AddOnsTemplate
      title="Professional Photographer Add-On for Your Tampa Charter"
      subtitle="Professional Photographer Add-On – Capture Every Moment"
      paragraphs={[
        <>
          You’ve seen the stunning shots on our website and
          social media—and if you want your own memories to
          look that good, a{' '}
          <LinkWithTitle to="/faqs/">
            Professional Photographer
          </LinkWithTitle>{' '}
          is a must-have add-on. These aren’t just nice
          vacation pictures; they’re dynamic,
          professional-quality moments that capture the real
          energy of your day on the water.
        </>,
        'These aren’t your average photographers. We partner with talented pros who specialize in promo, nightlife, and event photography. They know how to capture energy, movement, and moments that feel alive. Whether you’re cruising through the bay, splashing around the islands, or hitting up a local dockside bar, they’ll be there to document it all.',
        <>
          Your photographer will hop on board for the ride
          and follow the action wherever it flows—whether
          you're popping champagne, jumping in the water,
          dancing with your crew, or striking a pose with
          the{' '}
          <AnchorWithTitle href="https://www.visittampabay.com/map/">
            Tampa skyline
          </AnchorWithTitle>{' '}
          behind you. They’ll offer guidance when needed,
          help coordinate group shots, and make sure the
          lighting, angles, and moments are just right.
        </>,
        'Best of all, you can leave your phone in your bag and actually enjoy the day. No more worrying about water damage, shaky selfies, or missing the best shot. Let the photographer handle it while you live in the moment.',
        <>
          Each photo package includes dozens of
          professionally edited images that showcase your
          adventure from start to finish. From posed group
          shots to{' '}
          <LinkWithTitle to="/gallery/">
            candid party pics
          </LinkWithTitle>
          , you’ll have everything you need to relive your
          day—and share it, of course. Perfect for
          birthdays, proposals, bachelorette parties,
          branding shoots, or just capturing an epic day
          with friends.
        </>,
        'In short? Your day will look as good as it feels—and you won’t miss a moment of the action trying to capture it yourself.',
      ]}
    >
      <GalleriesList>
        <SwiperSlide>
          <GalleriesItem>
            <StaticImage
              alt={`${PROFESSIONAL_PHOTOGRAPHER_TITLE} slide 1`}
              src="../../images/special-events/fourth-of-july/exiting-stern-beer-can-island-high-tide-party.jpg"
            />
          </GalleriesItem>
        </SwiperSlide>
        <SwiperSlide>
          <GalleriesItem>
            <StaticImage
              alt={`${PROFESSIONAL_PHOTOGRAPHER_TITLE} slide 2`}
              src="../../images/special-events/fourth-of-july/models-taking-shots-tampa-bay-boating-adventures.jpg"
            />
          </GalleriesItem>
        </SwiperSlide>
      </GalleriesList>
    </AddOnsTemplate>
  );
};

export default AddOnsProfessionalPhotographer;
